import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const About = () => {
  const [navBackground, setNavBackground] = useState('rgba(255, 255, 255, 0.95)');
  const observerRef = useRef(null);

  useEffect(() => {
    // Scroll event listener for navbar
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setNavBackground('rgba(255, 255, 255, 0.98)');
      } else {
        setNavBackground('rgba(255, 255, 255, 0.95)');
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Intersection Observer for animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, observerOptions);

    // Observe elements
    const elements = document.querySelectorAll('.mission-card, .feature-item');
    elements.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      el.style.transition = 'opacity 0.6s, transform 0.6s';
      observerRef.current.observe(el);
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const smoothScroll = (e, targetId) => {
    e.preventDefault();
    const target = document.querySelector(targetId);
    if (target) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <div style={styles.body}>
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes floating {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        .floating {
          animation: floating 3s ease-in-out infinite;
        }
      `}</style>

      {/* Navigation */}

      <Header />
      {/* Hero Section */}
      <section style={styles.hero} id="home">
        <div style={styles.heroContent}>
          <h1 className="floating" style={styles.heroTitle}>About FigBot</h1>
          <p style={styles.subtitle}>
            We believe every business deserves intelligent support that works instantly — without complexity, setup time, or technical effort.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section style={styles.section} id="about">
        <div style={styles.container}>
          <div style={styles.missionGrid}>
            <div className="mission-card" style={styles.missionCard}>
              <h3 style={styles.missionCardTitle}>Our Vision</h3>
              <p style={styles.missionCardText}>
                FigBot was built with one simple vision: to make AI-powered customer support effortless. We're committed to building the simplest, cleanest, most intuitive AI experience in the world.
              </p>
            </div>
            <div className="mission-card" style={styles.missionCard}>
              <h3 style={styles.missionCardTitle}>5-Minute Setup</h3>
              <p style={styles.missionCardText}>
                Unlike traditional chatbots that demand hours of training and manual setup, FigBot does all the heavy lifting for you. Install in 5 minutes and start answering questions immediately.
              </p>
            </div>
            <div className="mission-card" style={styles.missionCard}>
              <h3 style={styles.missionCardTitle}>Smart & Automatic</h3>
              <p style={styles.missionCardText}>
                FigBot reads your entire website, understands your content, and automatically answers customer questions with unmatched accuracy — no manuals, no configuration, no intervention.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section style={styles.features} id="features">
        <div style={styles.container}>
          <h2 style={styles.featuresTitle}>Why Choose FigBot?</h2>
          <div style={styles.featureList}>
            <div className="feature-item" style={styles.featureItem}>
              <div style={styles.featureIcon}>⚡</div>
              <div style={styles.featureText}>
                <h4 style={styles.featureTextTitle}>Instant Intelligence</h4>
                <p style={styles.featureTextDescription}>
                  FigBot becomes your instant support partner — helping visitors find what they need, improving engagement, and reducing support workload from day one.
                </p>
              </div>
            </div>
            <div className="feature-item" style={styles.featureItem}>
              <div style={styles.featureIcon}>🎯</div>
              <div style={styles.featureText}>
                <h4 style={styles.featureTextTitle}>Zero Maintenance</h4>
                <p style={styles.featureTextDescription}>
                  No noise. No clutter. Just seamless intelligence that works in the background while you focus on growing your business.
                </p>
              </div>
            </div>
            <div className="feature-item" style={styles.featureItem}>
              <div style={styles.featureIcon}>🚀</div>
              <div style={styles.featureText}>
                <h4 style={styles.featureTextTitle}>Built for Everyone</h4>
                <p style={styles.featureTextDescription}>
                  Whether you're a startup, agency, or enterprise, FigBot empowers businesses of all sizes with a smart, reliable, always-on assistant.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={styles.ctaSection} id="contact">
        <div style={styles.container}>
          <h2 style={styles.ctaTitle}>Support made simple. AI made effortless.</h2>
          <p style={styles.ctaText}>Ready to transform your customer support experience?</p>
          <Link to="/signup" style={styles.ctaBtn}>Get Started Now</Link>
        </div>
      </section>

      {/* Footer */}
      {/* <footer style={styles.footer}>
        <p>&copy; 2025 FigBot. All rights reserved. Empowering businesses with effortless AI support.</p>
      </footer> */}
      <Footer/>
    </div>
  );
};

const styles = {
  body: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#333',
    overflowX: 'hidden',
    margin: 0,
    padding: 0,
  },
  nav: {
    position: 'fixed',
    top: 0,
    width: '100%',
    backdropFilter: 'blur(10px)',
    padding: '1.2rem 5%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 1000,
    boxShadow: '0 2px 20px rgba(0, 0, 0, 0.1)',
    transition: 'background 0.3s ease',
  },
  logoLink: {
    textDecoration: 'none',
  },
  logo: {
    fontSize: '1.8rem',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  navLinks: {
    display: 'flex',
    gap: '2rem',
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  navItem: {
    listStyle: 'none',
  },
  navLink: {
    textDecoration: 'none',
    color: '#333',
    fontWeight: 500,
    transition: 'color 0.3s',
  },
  hero: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6rem 5% 4rem',
    position: 'relative',
  },
  heroContent: {
    maxWidth: '900px',
    textAlign: 'center',
    animation: 'fadeInUp 1s ease-out',
  },
  heroTitle: {
    fontSize: '3.5rem',
    color: 'white',
    marginBottom: '1.5rem',
    lineHeight: 1.2,
  },
  subtitle: {
    fontSize: '1.4rem',
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: '3rem',
    lineHeight: 1.6,
  },
  section: {
    background: 'white',
    padding: '5rem 5%',
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  missionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '3rem',
    marginTop: '3rem',
  },
  missionCard: {
    padding: '2.5rem',
    borderRadius: '20px',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    transition: 'transform 0.3s, box-shadow 0.3s',
    cursor: 'pointer',
  },
  missionCardTitle: {
    fontSize: '1.8rem',
    color: '#667eea',
    marginBottom: '1rem',
  },
  missionCardText: {
    fontSize: '1.1rem',
    lineHeight: 1.7,
    color: '#555',
  },
  features: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '5rem 5%',
  },
  featuresTitle: {
    textAlign: 'center',
    fontSize: '2.8rem',
    marginBottom: '3rem',
  },
  featureList: {
    maxWidth: '800px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem',
  },
  featureItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '1.5rem',
    padding: '2rem',
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    borderRadius: '15px',
    transition: 'background 0.3s',
    cursor: 'pointer',
  },
  featureIcon: {
    fontSize: '2.5rem',
    flexShrink: 0,
  },
  featureText: {
    flex: 1,
  },
  featureTextTitle: {
    fontSize: '1.5rem',
    marginBottom: '0.5rem',
  },
  featureTextDescription: {
    fontSize: '1.1rem',
    lineHeight: 1.6,
    opacity: 0.9,
  },
  ctaSection: {
    background: 'white',
    padding: '5rem 5%',
    textAlign: 'center',
  },
  ctaTitle: {
    fontSize: '2.8rem',
    color: '#333',
    marginBottom: '1.5rem',
  },
  ctaText: {
    fontSize: '1.3rem',
    color: '#666',
    marginBottom: '2.5rem',
  },
  ctaBtn: {
    display: 'inline-block',
    padding: '1.2rem 3rem',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '50px',
    fontSize: '1.2rem',
    fontWeight: 600,
    transition: 'transform 0.3s, box-shadow 0.3s',
    boxShadow: '0 10px 30px rgba(102, 126, 234, 0.4)',
  },
  footer: {
    background: '#1a1a2e',
    color: 'white',
    textAlign: 'center',
    padding: '2rem 5%',
  },
};

export default About;

